import { useQuery } from '@apollo/client';
import { OndemandVideo } from '@mui/icons-material';
import { Box } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMovePlaylist } from '~/api/playlists';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import {
  BulkActions,
  BulkTagDialog,
  FilterAccordion,
  FilterChips,
  PlaylistGrid,
  Table,
} from '../list/components/';
import { PlaylistMoveDocument } from '../list/components/PlaylistMove.generated';
import { useFilters, useSelectedTagParams } from '../list/lib';
import { PlaylistSearchDocument } from '../queries/search.generated';

export const PlaylistSearch = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const backToFolder = searchParams.get('backToFolder')?.trim() ?? '';
  const view = searchParams.get('view')?.trim() || 'List';

  const link = useLink();
  const navigate = useNavigate();

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [tagPlaylistDialogOpen, setTagPlaylistDialogOpen] = useState(false);
  const [movePlaylistDialogOpen, setMovePlaylistDialogOpen] = useState(false);

  const [movePlaylist, movePlaylistInfo] = useMovePlaylist();

  const tags = useSelectedTagParams();

  const { data: playlistMoveData } = useQuery(PlaylistMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !movePlaylistDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      playlistMoveData?.network?.playlistFolders
        .filter((x) => x.canUpdate.value)
        .map((x) => x.id) ?? [],
    [playlistMoveData],
  );

  const { data, loading } = useQuery(PlaylistSearchDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, tags },
    skip: !search,
    onCompleted: (data) => handleOutOfBounds(data.network?.playlists.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.playlists.pageInfo.nodesCount);
  const playlists = useMemo(() => data?.network?.playlists.nodes ?? [], [data]);

  const playlistTags = useMemo(
    () => data?.network?.playlistTags.map((x) => x.name),
    [data?.network?.playlistTags],
  );
  const filterCounts = data?.network?.playlistFilterInfo;

  const { filter, setFilterItems, toggleAccordion, toggleItem, togglePanel } =
    useFilters(playlistTags);

  const filtersActive = useMemo(() => tags.length > 0, [tags]);

  const canDestroySelectedPlaylists = selectedIds.every((selectedId) => {
    const playlist = playlists.find((playlist) => playlist.id === selectedId);
    return playlist?.canDestroy.value;
  });

  const handleClearFilter = useCallback(() => {
    const folderId = backToFolder || folderTree.id;
    navigate(link(`/playlists/${folderId}`));
  }, [backToFolder, folderTree.id, link, navigate]);

  const handleSearch = useCallback(
    (newSearch: string) => {
      if (newSearch === '') {
        handleClearFilter();
        return;
      }
      setSearchParams((params) => {
        params.set('search', newSearch);
        params.set('backToFolder', backToFolder);
        return params;
      });
    },
    [backToFolder, handleClearFilter, setSearchParams],
  );

  return (
    <>
      <Helmet title={t('pages.playlists.search.title')} />
      <Toolbar titleIcon={<OndemandVideo />} titleText={t('pages.playlists.search.title')} />

      <ListPageContainer>
        <FilterSidebar open={filter.panelOpen} persistAs="playlistSearch:filterWidth">
          <FilterAccordion
            clearFilter={handleClearFilter}
            filter={filter}
            filterCounts={filterCounts}
            toggleAccordion={toggleAccordion}
            updateFilter={setFilterItems}
          />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle active={filtersActive} onToggle={togglePanel} />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.playlists.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 && (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagPlaylist={() => setTagPlaylistDialogOpen(true)}
                    onMovePlaylist={() => setMovePlaylistDialogOpen(true)}
                    canDestroySelectedPlaylists={canDestroySelectedPlaylists}
                  />
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="common.pluralize.playlist_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips items={filter.items || []} toggleItem={toggleItem} />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <Table
                    playlists={playlists}
                    folders={[]}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                    search
                  />
                ) : (
                  <PlaylistGrid
                    playlists={playlists}
                    folders={[]}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await movePlaylist({
                refetchQueries: ['PlaylistList'],
                variables: {
                  input: {
                    playlistFolderId: targetFolderId,
                    playlistIds: selectedIds as number[],
                  },
                },
              });
              setSelectedIds([]);
            }}
            loading={movePlaylistInfo.loading}
            onClose={() => setMovePlaylistDialogOpen(false)}
            open={movePlaylistDialogOpen}
            i18nKey="pages.playlists.dialogs.move_playlist.title"
          />

          {tagPlaylistDialogOpen && (
            <BulkTagDialog
              playlistIds={selectedIds as string[]}
              playlistTags={data?.network?.playlistTags.map((x) => x.name) || []}
              onClose={() => setTagPlaylistDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
